import * as THREE from 'three';
import { CORNERS, elevationToY, grassOn, seabedColor, terrainColor } from './HexMetrics';
import { axialKey, axialToWorld, neighbor } from './HexCoordinates';
import {
  HexCellData,
  cellOcclusion,
  centerKey,
  centerSlope,
  cornerElevation,
  groundLevel,
  cornerKey,
  cornerSlope,
  isWater,
} from './HexSurface';

/**
 * Builds one merged BufferGeometry out of a set of hex cells.
 *
 * Each cell is a fan of six triangles from its centre to its six corners. The
 * centre carries the cell's own elevation; the corners sit at the interpolated
 * elevation defined in HexSurface. Nothing joins the cells together because
 * nothing has to — neighbours reference the same corner vertices, so the
 * surface is continuous by construction.
 *
 * The geometry is indexed and corner vertices are shared between the three
 * cells meeting at them. That sharing is what makes the shading smooth across
 * cell boundaries.
 *
 * Normals are **not** taken from the triangles in this buffer. Six triangles
 * meet at a corner vertex, and a chunk holds only as many of them as happen to
 * belong to its own cells — so a vertex on a chunk border would be handed a
 * different normal by each side, and the seam would light up as a band even
 * though the two surfaces line up to the last decimal. They are computed from
 * the world instead, over every cell that touches this chunk, which is the same
 * principle that makes the heights agree: ask the data, not the partition.
 *
 * `members` and `world` are deliberately separate. A chunk is a render
 * partition, not a unit of data: it draws only its own cells, but reads
 * elevations from the whole map. That is what makes chunk seams disappear —
 * a vertex on a border is averaged from the same three cells whichever chunk
 * happens to compute it, so both arrive at the identical height without any
 * stitching or exchange between them.
 */
export class HexChunk {
  private readonly members: HexCellData[];
  private readonly world: Map<number, HexCellData>;
  private readonly maxElevation: number;
  private positions: number[] = [];
  private colors: number[] = [];
  /** Darń per wierzchołek — patrz `pushVertex`. */
  private grass: number[] = [];
  private indices: number[] = [];
  private cornerVertices = new Map<number, number>();
  /**
   * Occlusion per cell, worked out once and read seven times.
   *
   * Every cell's shade is asked for by its own centre and by the corners of
   * three cells, so without this the eighteen lookups it costs would be paid
   * four times over for the same answer.
   */
  private readonly occlusion = new Map<number, number>();

  /** Vertex identity per buffer index, for looking a normal up by vertex. */
  private vertexKeys: number[] = [];

  constructor(
    members: HexCellData[],
    world: Map<number, HexCellData>,
    maxElevation: number
  ) {
    this.members = members;
    this.world = world;
    this.maxElevation = maxElevation;
  }

  build(): THREE.BufferGeometry {
    this.positions = [];
    this.colors = [];
    this.grass = [];
    this.indices = [];
    this.vertexKeys = [];
    this.cornerVertices.clear();

    for (const cell of this.members) {
      const center = this.addCenterVertex(cell);

      for (let corner = 0; corner < 6; corner++) {
        this.indices.push(
          center,
          this.cornerVertex(cell, corner),
          this.cornerVertex(cell, (corner + 1) % 6)
        );
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(this.indices);
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(this.positions, 3)
    );
    geometry.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(this.colors, 3)
    );
    geometry.setAttribute('grass', new THREE.Float32BufferAttribute(this.grass, 1));
    geometry.setAttribute(
      'normal',
      new THREE.Float32BufferAttribute(this.buildNormals(), 3)
    );
    return geometry;
  }

  /** Centre vertices belong to a single cell, so they are never shared. */
  private addCenterVertex(cell: HexCellData): number {
    const { x, z } = axialToWorld(cell.q, cell.r);

    // Under water the terrain is seabed, drawn in sand — it is hidden beneath
    // an opaque surface today, but matches the shore if the water is ever made
    // translucent
    const color = isWater(cell)
      ? seabedColor()
      : terrainColor(
          cell.biome,
          cell.elevation,
          centerSlope(this.world, cell),
          this.maxElevation,
          !!cell.forest
        );

    // Ambient occlusion goes into the vertex colour rather than into a light:
    // it is a property of the ground, it costs nothing per frame, and it is
    // still there at midnight when the sun is not.
    color.multiplyScalar(this.shadeOf(cell));

    return this.pushVertex(
      x,
      elevationToY(groundLevel(cell)),
      z,
      color,
      centerKey(cell),
      // Pod wodą trawy nie ma — to jest dno w piasku, a nie łąka pod lustrem.
      isWater(cell) ? 0 : grassOn(cell.biome)
    );
  }

  /** Corner vertices are shared, so the first cell to reach one creates it. */
  private cornerVertex(cell: HexCellData, corner: number): number {
    const key = cornerKey(cell, corner);
    const existing = this.cornerVertices.get(key);
    if (existing !== undefined) return existing;

    const { x, z } = axialToWorld(cell.q, cell.r);
    const offset = CORNERS[corner];
    const elevation = cornerElevation(this.world, cell, corner);

    // A corner belongs to three cells, so it takes the biome of the one whose
    // fan is being drawn and lets the fans blend where they meet. The shore
    // needs no rule of its own any more: the generator marks beach cells from
    // which ones actually touch the sea, which is a better answer than a guess
    // made here from a vertex that happens to sit next to water — and it puts
    // sand on gentle shores without painting it onto sea cliffs.
    const slope = cornerSlope(this.world, cell, corner, elevation);
    const color = isWater(cell)
      ? seabedColor()
      : terrainColor(cell.biome, elevation, slope, this.maxElevation, !!cell.forest);

    // The mean of the three cells meeting here, like the elevation: all three
    // fans have to agree on it or the shading breaks along the cell boundary.
    color.multiplyScalar(this.cornerShade(cell, corner));

    const index = this.pushVertex(
      x + offset.x,
      elevationToY(elevation),
      z + offset.z,
      color,
      key,
      isWater(cell) ? 0 : grassOn(cell.biome)
    );
    this.cornerVertices.set(key, index);
    return index;
  }

  /** Occlusion of one cell, computed once. */
  private shadeOf(cell: HexCellData): number {
    const key = axialKey(cell.q, cell.r);
    const known = this.occlusion.get(key);
    if (known !== undefined) return known;

    const shade = cellOcclusion(this.world, cell);
    this.occlusion.set(key, shade);
    return shade;
  }

  private cornerShade(cell: HexCellData, corner: number): number {
    let sum = 0;
    let count = 0;

    for (const at of [cell, neighbor(cell, (corner + 5) % 6), neighbor(cell, corner)]) {
      const there = this.world.get(axialKey(at.q, at.r));
      if (!there) continue;
      sum += this.shadeOf(there);
      count++;
    }

    return count === 0 ? 1 : sum / count;
  }

  private pushVertex(
    x: number,
    y: number,
    z: number,
    color: THREE.Color,
    key: number,
    /**
     * Czy pod tym wierzchołkiem jest darń, 1 albo 0.
     *
     * Interpolowane jak kolor, więc na granicy łąki z piaskiem trawa **gaśnie
     * płynnie** i nie ma czego dopisywać: to samo dzielenie wierzchołków, które
     * wygładza cieniowanie, wygładza i ją.
     */
    grass: number
  ): number {
    const index = this.positions.length / 3;
    this.positions.push(x, y, z);
    this.colors.push(color.r, color.g, color.b);
    this.grass.push(grass);
    this.vertexKeys.push(key);
    return index;
  }

  /**
   * Normals for every vertex, summed over the whole surface around it.
   *
   * The fans of this chunk's cells *and* of the ring of cells around it are
   * walked, so a border vertex receives all six of the triangles that actually
   * meet there rather than the two or four this chunk happens to draw. The
   * cross products are added unnormalised, which weights each face by its area
   * — the same averaging computeVertexNormals does, only over the right set of
   * faces.
   *
   * At the edge of the world some of those neighbours do not exist. The sum is
   * then over what is there, which is the same answer both sides would reach,
   * because there is no other side.
   */
  private buildNormals(): number[] {
    const summed = new Map<number, THREE.Vector3>();
    const walked = new Set<number>();

    for (const cell of this.members) {
      this.accumulate(cell, summed, walked);

      for (let direction = 0; direction < 6; direction++) {
        const around = neighbor(cell, direction);
        const data = this.world.get(axialKey(around.q, around.r));
        if (data) this.accumulate(data, summed, walked);
      }
    }

    const normals: number[] = [];
    for (const key of this.vertexKeys) {
      const sum = summed.get(key);
      if (!sum || sum.lengthSq() === 0) {
        normals.push(0, 1, 0);
        continue;
      }
      const unit = sum.clone().normalize();
      normals.push(unit.x, unit.y, unit.z);
    }

    return normals;
  }

  /** Add one cell's six faces to the running total at each of their vertices. */
  private accumulate(
    cell: HexCellData,
    summed: Map<number, THREE.Vector3>,
    walked: Set<number>
  ): void {
    const identity = centerKey(cell);
    if (walked.has(identity)) return;
    walked.add(identity);

    const { x, z } = axialToWorld(cell.q, cell.r);
    const center = new THREE.Vector3(x, elevationToY(groundLevel(cell)), z);

    const corners: THREE.Vector3[] = [];
    const keys: number[] = [];
    for (let corner = 0; corner < 6; corner++) {
      const offset = CORNERS[corner];
      corners.push(
        new THREE.Vector3(
          x + offset.x,
          elevationToY(cornerElevation(this.world, cell, corner)),
          z + offset.z
        )
      );
      keys.push(cornerKey(cell, corner));
    }

    const edge1 = new THREE.Vector3();
    const edge2 = new THREE.Vector3();
    const face = new THREE.Vector3();

    for (let corner = 0; corner < 6; corner++) {
      const next = (corner + 1) % 6;

      // Wound the same way the indices are, so the faces point up
      edge1.subVectors(corners[corner], center);
      edge2.subVectors(corners[next], center);
      face.crossVectors(edge1, edge2);

      for (const key of [identity, keys[corner], keys[next]]) {
        const total = summed.get(key);
        if (total) total.add(face);
        else summed.set(key, face.clone());
      }
    }
  }
}
